/**
 * Google Tag Manager — Consent Mode v2 wiring.
 *
 * The container is loaded on every page via `<GoogleTagManager />` in the
 * root layout. Consent defaults to `denied` (Consent Mode v2), so tags
 * fire in cookieless-ping mode initially — letting Google's modelled
 * conversions fill the gap for users who never interact with the banner.
 *
 * Categories map to Google Consent Mode v2 signals:
 *   analytics  → analytics_storage
 *   marketing  → ad_storage + ad_user_data + ad_personalization
 *   functional → functionality_storage + personalization_storage
 */

export const GTM_ID = "GTM-PQ66N64Z";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Push a Consent-Mode-v2 `update` for the granted categories. Safe to call
 *  whether or not GTM is loaded — `gtag` is defined inline very early. */
export function updateConsent(categories: ReadonlyArray<string>): void {
  if (typeof window === "undefined") return;
  const g = (granted: boolean) => (granted ? "granted" : "denied");
  const analytics = categories.includes("analytics");
  const marketing = categories.includes("marketing");
  const functional = categories.includes("functional");
  window.gtag?.("consent", "update", {
    analytics_storage: g(analytics),
    ad_storage: g(marketing),
    ad_user_data: g(marketing),
    ad_personalization: g(marketing),
    functionality_storage: g(functional),
    personalization_storage: g(functional),
  });
  window.dataLayer?.push({ event: "cookie_consent_update", categories });
}

/**
 * Lead-submit conversion event. Fired ONLY on confirmed API success
 * (HTTP 2xx) from a lead form. GTM picks this up via a Custom Event
 * trigger named `lead_submit` and routes it to Google Ads (Conversion
 * Linker + Conversion tag) and GA4 (event tag).
 *
 * Enhanced Conversions for Leads: when the caller passes `userData`,
 * the raw values are SHA-256 hashed in the browser via `hashUserData`
 * and pushed under `user_data` so the Google Ads Conversion tag can
 * forward them to Google for offline match-back. Raw PII never reaches
 * the dataLayer — only hashes.
 */
import { hashUserData, type RawUserData } from "./hash";

export type LeadForm = "quote" | "callback" | "contact" | "carrier";

export type LeadSubmitParams = {
  form: LeadForm;
  shipmentKind?: string;
  fromCountry?: string;
  toCountry?: string;
  callbackRequested?: boolean;
  topic?: string;
  /** Raw user-provided data — hashed in-browser before pushing to dataLayer. */
  userData?: RawUserData;
};

export async function pushLeadSubmit(p: LeadSubmitParams): Promise<void> {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer ?? [];
  const user_data = p.userData ? await hashUserData(p.userData) : undefined;
  window.dataLayer.push({
    event: "lead_submit",
    lead_form: p.form,
    shipment_kind: p.shipmentKind,
    from_country: p.fromCountry,
    to_country: p.toCountry,
    callback_requested: p.callbackRequested,
    topic: p.topic,
    user_data,
  });
}

/**
 * /angebot booking funnel — step-view events using GA4 ecommerce semantic
 * names so GTM and GA4 recognize them as funnel stages.
 *
 *   "1.1" → view_item          (entered the funnel, addresses picker shown)
 *   "1.2" → add_to_cart        (cargo entered, "the item" defined)
 *   "1.3" → select_item        (vehicle picked, shipping method)
 *   "2"   → add_shipping_info  (full address + contact details)
 *   "3"   → add_payment_info   (payment screen shown)
 *
 * `value` is the running quote total at that step. Optional — earliest
 * steps don't have one yet.
 */
export type BookingStep = "1.1" | "1.2" | "1.3" | "1.4" | "2" | "3";

const STEP_TO_EVENT: Record<BookingStep, string> = {
  "1.1": "view_item",
  "1.2": "add_to_cart",
  "1.3": "select_item",
  "1.4": "view_item_list",
  "2": "add_shipping_info",
  "3": "add_payment_info",
};

const STEP_LABELS: Record<BookingStep, string> = {
  "1.1": "Adressen",
  "1.2": "Cargo",
  "1.3": "Fahrzeug",
  "1.4": "Zusatzservices",
  "2": "Versanddetails",
  "3": "Bezahlung",
};

export type BookingStepParams = {
  value?: number;
  currency?: string;
  shipmentKind?: string;
  fromCountry?: string;
  toCountry?: string;
};

export function pushBookingStep(step: BookingStep, p: BookingStepParams = {}): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({
    event: STEP_TO_EVENT[step],
    booking_step: step,
    step_name: STEP_LABELS[step],
    value: p.value,
    currency: p.currency ?? "EUR",
    shipment_kind: p.shipmentKind,
    from_country: p.fromCountry,
    to_country: p.toCountry,
  });
}

/**
 * Stripe payment success — fires GA4 `purchase` with the real transaction
 * value so Google Ads can shift to value-based bidding later.
 */
export type PurchaseParams = {
  transactionId: string;
  value: number;
  currency?: string;
  paymentMethod?: string;
  shipmentKind?: string;
  fromCountry?: string;
  toCountry?: string;
  userData?: RawUserData;
};

export async function pushPurchase(p: PurchaseParams): Promise<void> {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer ?? [];
  const user_data = p.userData ? await hashUserData(p.userData) : undefined;
  window.dataLayer.push({
    event: "purchase",
    transaction_id: p.transactionId,
    value: p.value,
    currency: p.currency ?? "EUR",
    payment_method: p.paymentMethod,
    shipment_kind: p.shipmentKind,
    from_country: p.fromCountry,
    to_country: p.toCountry,
    user_data,
  });
}

/**
 * Invoice booking submission (no immediate payment) — fires GA4
 * `generate_lead` so it's distinct from marketing-form `lead_submit`.
 */
export type GenerateLeadParams = {
  value?: number;
  shipmentKind?: string;
  fromCountry?: string;
  toCountry?: string;
  userData?: RawUserData;
};

export async function pushGenerateLead(p: GenerateLeadParams): Promise<void> {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer ?? [];
  const user_data = p.userData ? await hashUserData(p.userData) : undefined;
  window.dataLayer.push({
    event: "generate_lead",
    value: p.value ?? 25,
    currency: "EUR",
    shipment_kind: p.shipmentKind,
    from_country: p.fromCountry,
    to_country: p.toCountry,
    user_data,
  });
}
